import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/App';
import { 
  Waves, LogOut, Menu, X, Droplets, LayoutDashboard, 
  ShoppingCart, Settings, ChevronLeft, FileText,
  Clock, HelpCircle, Fish
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';

export default function CustomerNavigation({ companyName }) {
  const location = useLocation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  
  useEffect(() => {
    const handleResize = () => { if (window.innerWidth < 1024) setCollapsed(true); };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const handleLogout = async () => { await logout(); navigate('/'); };
  
  const isActive = (path) => {
    if (path === '/dashboard' && location.pathname === '/dashboard') return true;
    if (path !== '/dashboard' && location.pathname.startsWith(path)) return true;
    return false;
  };
  
  const navItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Oversikt' },
    { path: '/dashboard/bulk-history', icon: Clock, label: 'Historikk' },
    { path: '/dashboard/reports', icon: FileText, label: 'Rapporter' },
    { path: '/customer/orders', icon: ShoppingCart, label: 'Bestillinger' },
    { path: '/customer/fish-health', icon: Fish, label: 'Observasjonar' },
  ];

  const bottomItems = [
    { path: '/customer/settings', icon: Settings, label: 'Innstillinger' },
    { path: '/customer/help', icon: HelpCircle, label: 'Hjelp' },
  ];
  
  const SidebarContent = ({ isMobile = false }) => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className={`flex items-center ${collapsed && !isMobile ? 'justify-center' : 'justify-between'} p-4 border-b border-slate-100`}>
        <Link to="/dashboard" className="flex items-center gap-3">
          <div className="w-9 h-9 bg-teal-500 rounded-lg flex items-center justify-center flex-shrink-0">
            <Waves className="w-5 h-5 text-white" />
          </div>
          {(!collapsed || isMobile) && (
            <div>
              <span className="font-semibold text-slate-900">Microsafe</span>
              {companyName && <span className="block text-xs text-slate-400">{companyName}</span>}
            </div>
          )}
        </Link>
        {!isMobile && (
          <button 
            onClick={() => setCollapsed(!collapsed)}
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors hidden lg:flex"
          >
            <ChevronLeft className={`w-4 h-4 transition-transform ${collapsed ? 'rotate-180' : ''}`} />
          </button>
        )}
      </div>
      
      {/* Order Sample Button */}
      <div className="p-3">
        <Link
          to="/customer/order-sample"
          className={`flex items-center gap-2 bg-teal-500 hover:bg-teal-600 text-white rounded-lg transition-colors ${
            collapsed && !isMobile ? 'justify-center p-2.5' : 'px-4 py-2.5'
          }`}
        >
          <Droplets className="w-4 h-4" />
          {(!collapsed || isMobile) && <span className="font-medium text-sm">Bestill prøve</span>}
        </Link>
      </div>
      
      {/* Main Navigation */}
      <nav className="flex-1 px-3 py-2 space-y-1 overflow-y-auto">
        {navItems.map(({ path, icon: Icon, label }) => (
          <Link
            key={path}
            to={path}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-sm ${
              isActive(path)
                ? 'bg-teal-50 text-teal-700 font-medium'
                : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
            } ${collapsed && !isMobile ? 'justify-center' : ''}`}
            title={collapsed && !isMobile ? label : undefined}
          >
            <Icon className={`w-[18px] h-[18px] flex-shrink-0 ${isActive(path) ? 'text-teal-600' : ''}`} />
            {(!collapsed || isMobile) && <span>{label}</span>}
          </Link>
        ))}
      </nav>

      {/* Bottom Items */}
      <div className="px-3 py-2 border-t border-slate-100 space-y-1">
        {bottomItems.map(({ path, icon: Icon, label }) => (
          <Link
            key={path}
            to={path}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-sm ${
              isActive(path)
                ? 'bg-teal-50 text-teal-700 font-medium'
                : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
            } ${collapsed && !isMobile ? 'justify-center' : ''}`}
          >
            <Icon className={`w-[18px] h-[18px] flex-shrink-0 ${isActive(path) ? 'text-teal-600' : ''}`} />
            {(!collapsed || isMobile) && <span>{label}</span>}
          </Link>
        ))}
      </div>
      
      {/* User */}
      <div className="p-3 border-t border-slate-100">
        <div className={`flex items-center gap-3 p-2 rounded-lg ${collapsed && !isMobile ? 'justify-center' : ''}`}>
          <Avatar className="h-8 w-8 flex-shrink-0">
            <AvatarFallback className="bg-teal-100 text-teal-700 text-xs font-medium">
              {user?.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U'}
            </AvatarFallback>
          </Avatar>
          {(!collapsed || isMobile) && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{user?.name || 'Bruker'}</p>
              <p className="text-xs text-slate-400 truncate">{user?.email}</p>
            </div>
          )}
        </div>
        <button
          onClick={handleLogout}
          className={`flex items-center gap-3 w-full px-3 py-2 mt-2 text-sm text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors ${
            collapsed && !isMobile ? 'justify-center' : ''
          }`}
        >
          <LogOut className="w-4 h-4" />
          {(!collapsed || isMobile) && <span>Logg ut</span>}
        </button>
      </div>
    </div>
  );
  
  return (
    <>
      {/* Desktop Sidebar */}
      <aside className={`hidden lg:flex flex-col fixed left-0 top-0 h-screen bg-white border-r border-slate-100 z-40 transition-all duration-300 ${
        collapsed ? 'w-[70px]' : 'w-[240px]'
      }`}>
        <SidebarContent />
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-white border-b border-slate-100 z-40 flex items-center justify-between px-4">
        <Link to="/dashboard" className="flex items-center gap-2">
          <div className="w-8 h-8 bg-teal-500 rounded-lg flex items-center justify-center">
            <Waves className="w-4 h-4 text-white" />
          </div>
          <span className="font-semibold text-slate-900 text-sm">Microsafe</span>
        </Link>
        <button 
          onClick={() => setMobileOpen(!mobileOpen)}
          className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 transition-colors"
        >
          {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <div className="absolute top-0 left-0 w-[280px] h-full bg-white shadow-xl">
            <SidebarContent isMobile />
          </div>
        </div>
      )}

      {/* Content Spacer */}
      <div className={`hidden lg:block ${collapsed ? 'w-[70px]' : 'w-[240px]'} flex-shrink-0 transition-all duration-300`} />
    </>
  );
}
